import React from "react";
import { Field as FormikControl, Form, Formik, FormikConfig } from "formik";
import classNames from "classnames";
import { format, isSameDay } from "date-fns";
import {
    currencyFormatterFractionsIfNeeded,
    currencyFormatterNoFractions,
} from "@common/helpers/currency";
import { FlatRatePrice } from "@common/flatRateBilling";
import { FlatRatePlan } from "@common/schemas/billing.schema";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { Card } from "@design-system/Card/Card";
import { useConstant } from "@hooks/useConstant";
import { discountLabel } from "@helpers/billing";
import { Button } from "@design-system/Button/Button";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import styles from "./UpgradeSubscriptionModal.module.scss";

interface Props {
    plan: FlatRatePlan;
    pricing: FlatRatePrice;
    discountCode: string | null;
    onDiscountCodeChange: (code: string | null) => void;
    codeInvalid?: boolean;
    billingDate: Date;
}

export const UpgradeModalFooter: React.FC<Props> = ({
    discountCode,
    onDiscountCodeChange,
    codeInvalid,
    pricing,
    billingDate,
}) => {
    const { total, totalBeforeDiscount } = pricing;
    const today = useConstant(() => new Date());

    const form: FormikConfig<{ code: string }> = {
        initialValues: { code: discountCode ?? "" },
        onSubmit: ({ code }) => {
            if (code) {
                onDiscountCodeChange(code);
            }
        },
        onReset() {
            onDiscountCodeChange(null);
        },
    };

    const futureBilling = !isSameDay(billingDate, today);
    const activeDiscount = pricing.discount;

    return (
        <Card className={classNames(styles.footer, "p-0")}>
            <section className={styles.section}>
                <Formik {...form}>
                    {({ resetForm }) => (
                        <Form className={styles.form}>
                            <Field.Root invalid={Boolean(codeInvalid)}>
                                <FormikControl
                                    as={Input}
                                    name="code"
                                    placeholder="Enter discount code"
                                    size="lg"
                                    readOnly={!!discountCode}
                                    invalid={Boolean(codeInvalid)}
                                    data-testid="coupon-input"
                                />
                                {codeInvalid ? (
                                    <Field.Error match>
                                        Invalid or expired code
                                    </Field.Error>
                                ) : null}
                            </Field.Root>

                            {discountCode ? (
                                <Button
                                    onClick={() => resetForm()}
                                    variant="primary"
                                    size="md"
                                    data-testid="remove-coupon-button"
                                >
                                    Remove
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="md"
                                    type="submit"
                                    data-testid="submit-coupon-button"
                                >
                                    Add
                                </Button>
                            )}
                        </Form>
                    )}
                </Formik>
            </section>

            {activeDiscount ? (
                <section className={styles.section}>
                    <main className={styles.info}>Applied discount</main>

                    <aside className={styles.pricing}>
                        {discountLabel(activeDiscount)}
                    </aside>
                </section>
            ) : null}

            {futureBilling ? (
                <section className={styles.section}>
                    <main className={styles.info}>Total for today</main>

                    <aside className={classNames(styles.pricing, styles.value)}>
                        {currencyFormatterNoFractions.format(0)}
                    </aside>
                </section>
            ) : null}

            <section className={styles.section}>
                <main className={styles.info}>
                    Total{" "}
                    {futureBilling
                        ? `on ${format(
                              pricing.nextBillingDate,
                              fullDateFullYearFormat,
                          )}`
                        : "for today"}
                </main>

                <aside className={classNames(styles.pricing, styles.value)}>
                    <div className={styles.totals}>
                        {totalBeforeDiscount !== total ? (
                            <span className={styles.discountedTotal}>
                                {currencyFormatterFractionsIfNeeded.format(
                                    fromIntToMonetary(totalBeforeDiscount),
                                )}
                            </span>
                        ) : null}

                        <span className={styles.total}>
                            {total < 0 ? (
                                <>
                                    {currencyFormatterFractionsIfNeeded.format(
                                        fromIntToMonetary(0),
                                    )}

                                    <div className={styles.info}>
                                        Credit:{" "}
                                        {currencyFormatterFractionsIfNeeded.format(
                                            fromIntToMonetary(Math.abs(total)),
                                        )}{" "}
                                        applied to your next payment
                                    </div>
                                </>
                            ) : (
                                <>
                                    {currencyFormatterFractionsIfNeeded.format(
                                        fromIntToMonetary(total),
                                    )}
                                </>
                            )}
                        </span>
                    </div>
                </aside>
            </section>
        </Card>
    );
};
